import { BoxProps } from '@chakra-ui/react'
import { mdiExport, mdiFileExcelOutline, mdiFilePowerpointOutline, mdiFileWordOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { ComponentProps, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { IconButton } from '@/components/ui/button'
import { MenuContent, MenuItem, MenuItemGroup, MenuItemText, MenuRoot, MenuTrigger } from '@/components/ui/menu'
import { currentEnv } from '@/env/vars'
import useTrackExportJob from '@/hooks/useTrackExportJob'

import { exportMessageToDocx, exportMessageToPptx, exportMessageToXlsx } from './ExportUtils'

const DEFAULT_TRIGGER_ICON_SIZE = '18px'
const DEFAULT_MENU_ICON_SIZE = 1

export interface IMessageExportProps extends BoxProps {
    message: string
    userPromptId?: number | null
    size?: ComponentProps<typeof Icon>['size']
    menuIconSize?: ComponentProps<typeof Icon>['size']
}

const MessageExport = ({ message, userPromptId, size, menuIconSize, ...props }: IMessageExportProps) => {
    const { t } = useTranslation()
    const isMessageExportEnabled = currentEnv.features.isEnabled('messageExport') && !!userPromptId

    const { addJob, busy } = useTrackExportJob(userPromptId ?? 0)

    const handleExportExcel = useCallback(() => {
        addJob(exportMessageToXlsx({ message }))
    }, [addJob, message])

    const handleExportDocx = useCallback(() => {
        addJob(exportMessageToDocx({ message }))
    }, [addJob, message])

    const handleExportPptx = useCallback(() => {
        addJob(exportMessageToPptx({ message }))
    }, [addJob, message])

    return isMessageExportEnabled ? (
        <Box {...props} data-testid="chat-actions-export" display="flex" alignItems="center">
            <MenuRoot variant="export">
                <MenuTrigger asChild data-testid="export-message-menu-trigger" rounded="full">
                    <IconButton
                        rounded="full"
                        variant="menuTrigger"
                        loading={busy}
                        icon={<Icon path={mdiExport} size={size ?? DEFAULT_TRIGGER_ICON_SIZE} />}
                        aria-label={t('chat.export.menu.label')}
                    />
                </MenuTrigger>
                <MenuContent>
                    <MenuItemGroup title={t('chat.export.menu.options.groupTitle.label')}></MenuItemGroup>
                    <MenuItem data-testid="export-messge-xlsx" onClick={handleExportExcel} value="export-xlsx">
                        <Icon size={menuIconSize ?? DEFAULT_MENU_ICON_SIZE} path={mdiFileExcelOutline} />
                        <MenuItemText>{t('chat.export.menu.options.xlsx.label')}</MenuItemText>
                    </MenuItem>
                    <MenuItem data-testid="export-message-docx" onClick={handleExportDocx} value="export-docx">
                        <Icon size={menuIconSize ?? DEFAULT_MENU_ICON_SIZE} path={mdiFileWordOutline} />
                        <MenuItemText>{t('chat.export.menu.options.docx.label')}</MenuItemText>
                    </MenuItem>
                    <MenuItem data-testid="export-message-pptx" onClick={handleExportPptx} value="export-pptx">
                        <Icon size={menuIconSize ?? DEFAULT_MENU_ICON_SIZE} path={mdiFilePowerpointOutline} />
                        <MenuItemText>{t('chat.export.menu.options.pptx.label')}</MenuItemText>
                    </MenuItem>
                </MenuContent>
            </MenuRoot>
        </Box>
    ) : null
}

export default MessageExport
